import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import RouteMap from '@/components/common/RouteMap';
import RouteStopTasks from '@/components/operations/RouteStopTasks';
import { MapPin, Search, Plus, X, ChevronUp, ChevronDown, Loader2, Route as RouteIcon, AlertTriangle, Check, RotateCcw, Clock3, Save, FolderOpen, Trash2, Pencil, Maximize2, Minimize2, CalendarPlus } from 'lucide-react';
import { ACTION_LABELS } from '@/components/operations/routeTaskCatalog';

const today = () => new Date().toISOString().slice(0, 10);
const STOP_COLORS = { assigned: '#64748B', in_progress: '#1C4FC9', completed: '#5FA718', unable: '#C8641E' };

const RoutePlanning = () => {
  const { token } = useContext(AuthContext);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [locs, setLocs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [search, setSearch] = useState('');
  const [fCounty, setFCounty] = useState('');
  const [fCity, setFCity] = useState('');
  const [fZip, setFZip] = useState('');
  const [selected, setSelected] = useState([]);   // [{...loc}]
  const [routeNo, setRouteNo] = useState('');
  const [routeName, setRouteName] = useState('');
  const [date, setDate] = useState(today());
  const [techId, setTechId] = useState('');
  const [geocoding, setGeocoding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewRoute, setViewRoute] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [savingTpl, setSavingTpl] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadLocs = (q = '') => axios.get(`${API}/routes/service-locations${q ? `?search=${encodeURIComponent(q)}` : ''}`, h).then(r => setLocs(r.data || [])).catch(() => {});
  const loadRoutes = () => axios.get(`${API}/routes`, h).then(r => setRoutes(r.data || [])).catch(() => {});
  const loadTemplates = () => axios.get(`${API}/routes/templates`, h).then(r => setTemplates(r.data || [])).catch(() => {});
  useEffect(() => { loadLocs(); loadRoutes(); loadTemplates(); axios.get(`${API}/routes/technicians`, h).then(r => setTechs(r.data || [])).catch(() => {}); /* eslint-disable-next-line */ }, [token]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return locs.filter(l =>
      (!s || `${l.location_name} ${l.address} ${l.account_number || ''} ${l.customer_name || ''}`.toLowerCase().includes(s))
      && (!fCounty || l.county === fCounty)
      && (!fCity || l.city === fCity)
      && (!fZip || (l.zip || '').includes(fZip)));
  }, [locs, search, fCounty, fCity, fZip]);

  const counties = useMemo(() => [...new Set(locs.map(l => l.county).filter(Boolean))].sort(), [locs]);
  const cities = useMemo(() => [...new Set(locs.map(l => l.city).filter(Boolean))].sort(), [locs]);

  const isSel = (id) => selected.some(s => s.id === id);

  const geocodeOne = async (l) => {
    if (!['needs_geocode', 'failed', 'rate_limited'].includes(l.geo_state)) return l;
    try {
      const r = await axios.post(`${API}/routes/geocode`, { location_ids: [l.id] }, h);
      const res = (r.data.results || {})[l.id];
      if (res && res.status === 'ok') {
        const upd = { ...l, lat: res.lat, lng: res.lng, mappable: true, geo_state: 'mapped', needs_geocode: false };
        setLocs(prev => prev.map(x => x.id === l.id ? upd : x));
        return upd;
      }
      if (res && (res.status === 'rate_limited' || res.status === 'service_error')) {
        toast.warning(`Map service is busy — ${l.location_name} will stay selectable, try plotting again shortly`);
        setLocs(prev => prev.map(x => x.id === l.id ? { ...x, geo_state: 'rate_limited', needs_geocode: false } : x));
        return { ...l, geo_state: 'rate_limited' };
      }
      toast.error(`Could not map ${l.location_name} (${res?.status || 'failed'})`);
      setLocs(prev => prev.map(x => x.id === l.id ? { ...x, geo_state: 'failed', needs_geocode: false } : x));
      return { ...l, geo_state: 'failed' };
    } catch { toast.error('Geocoding failed — try again'); return l; }
  };

  const toggle = async (l) => {
    setFocusId(l.id);
    if (isSel(l.id)) { setSelected(prev => prev.filter(s => s.id !== l.id)); return; }
    const loc = ['needs_geocode', 'failed', 'rate_limited'].includes(l.geo_state) ? await geocodeOne(l) : l;
    setSelected(prev => prev.some(s => s.id === loc.id) ? prev : [...prev, { ...loc, tasks: loc.tasks || [], notes: loc.notes || '' }]);
  };
  const onMapPick = (id) => { const l = locs.find(x => x.id === id) || selected.find(s => s.id === id); if (l) toggle(l); };
  const resetDraft = () => {
    if ((selected.length || routeNo || routeName || techId || editingTemplateId) && !window.confirm('Discard this unsaved route draft? Saved templates and assigned routes are not affected.')) return;
    setSelected([]); setRouteNo(''); setRouteName(''); setTechId(''); setDate(today());
    setEditingTemplateId(null); setTemplateName('');
    toast.success('Draft cleared');
  };
  const move = (i, d) => setSelected(prev => { const n = [...prev]; const j = i + d; if (j < 0 || j >= n.length) return prev; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const setStopField = (id, k, v) => setSelected(prev => prev.map(s => s.id === id ? { ...s, [k]: v } : s));

  const loadTemplate = async (tid, forAssign = false) => {
    try {
      const r = await axios.get(`${API}/routes/templates/${tid}`, h);
      const t = r.data;
      setSelected(t.stops.map(s => ({
        id: s.location_id, location_name: s.location_name || '(removed location)', account_number: s.account_number,
        customer_name: s.customer_name, address: s.address, lat: s.lat, lng: s.lng,
        mappable: s.mappable, geo_state: s.mappable ? 'mapped' : 'needs_geocode',
        tasks: (s.tasks || []).map(x => ({ key: x.key, label: x.label, category: x.category, action: x.action || 'inspect' })),
        notes: s.notes || s.instructions || '', missing: s.missing,
      })));
      setEditingTemplateId(t.id); setTemplateName(t.name); setRouteName(t.description || '');
      setRouteNo(t.name);   // default the dated route name to the template name (editable)
      if (forAssign) { setDate(today()); setTimeout(() => document.querySelector('[data-testid="rp-builder"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }
      toast.success(forAssign ? `Loaded "${t.name}" — choose a technician & date, then Save & Assign` : `Editing template "${t.name}"`);
    } catch { toast.error('Could not load template'); }
  };
  const saveTemplate = async () => {
    if (!templateName.trim()) return toast.error('Enter a template name');
    if (!selected.length) return toast.error('Select at least one stop');
    setSavingTpl(true);
    const payload = {
      name: templateName.trim(), description: routeName || '',
      stops: selected.map(s => ({ location_id: s.id, notes: s.notes || '', tasks: (s.tasks || []).map(t => ({ key: t.key, label: t.label, category: t.category, action: t.action })) })),
    };
    try {
      if (editingTemplateId) { await axios.put(`${API}/routes/templates/${editingTemplateId}`, payload, h); toast.success('Template updated'); }
      else { const r = await axios.post(`${API}/routes/templates`, payload, h); setEditingTemplateId(r.data.id); toast.success('Template saved'); }
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSavingTpl(false);
  };
  const deleteTemplate = async (tid, name) => {
    if (!window.confirm(`Delete template "${name}"? This does not delete any service location or assigned route.`)) return;
    try { await axios.delete(`${API}/routes/templates/${tid}`, h); if (editingTemplateId === tid) { setEditingTemplateId(null); setTemplateName(''); } loadTemplates(); toast.success('Template deleted'); }
    catch { toast.error('Delete failed'); }
  };

  const plot = async () => {
    const ids = filtered.slice(0, 20).map(l => l.id).filter(id => { const l = locs.find(x => x.id === id); return l && !l.mappable && l.address_complete; });
    if (!ids.length) { toast.message('Nothing new to geocode in view'); return; }
    setGeocoding(true);
    try { await axios.post(`${API}/routes/geocode`, { location_ids: ids }, h); await loadLocs(search); toast.success('Map updated'); }
    catch { toast.error('Geocoding failed'); }
    setGeocoding(false);
  };

  const save = async () => {
    const rn = (routeNo.trim() || templateName.trim() || routeName.trim());
    if (!rn) return toast.error('Enter a route name');
    if (!techId) return toast.error('Choose a technician');
    if (!selected.length) return toast.error('Select at least one stop');
    setSaving(true);
    try {
      await axios.post(`${API}/routes`, {
        route_number: rn, route_name: routeName, date, technician_id: techId,
        stops: selected.map(s => ({ location_id: s.id, notes: s.notes || '', tasks: (s.tasks || []).map(t => ({ key: t.key, label: t.label, category: t.category, action: t.action })) })),
      }, h);
      toast.success('Route assigned');
      setSelected([]); setRouteNo(''); setRouteName(''); setTechId(''); setEditingTemplateId(null); setTemplateName('');
      loadRoutes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const selOrder = useMemo(() => { const m = {}; selected.forEach((s, i) => { m[s.id] = i + 1; }); return m; }, [selected]);
  const mapPoints = useMemo(() => {
    const base = filtered.filter(l => l.mappable).map(l => ({
      id: l.id, lat: l.lat, lng: l.lng, label: l.location_name, address: l.address,
      customer_name: l.customer_name, account_number: l.account_number, order: selOrder[l.id] || null,
    }));
    const seen = new Set(base.map(b => b.id));
    selected.forEach(s => { if (s.lat != null && !seen.has(s.id)) base.push({ id: s.id, lat: s.lat, lng: s.lng, label: s.location_name, address: s.address, customer_name: s.customer_name, account_number: s.account_number, order: selOrder[s.id] }); });
    return base;
  }, [filtered, selected, selOrder]);
  const fitSignature = useMemo(() => `${fCounty}|${fCity}|${fZip}|${search}|${filtered.filter(l => l.mappable).length}`, [fCounty, fCity, fZip, search, filtered]);

  const incomplete = filtered.filter(l => l.geo_state === 'incomplete');

  if (viewRoute) {
    const r = viewRoute;
    return (
      <div className="p-4 md:p-6" data-testid="route-detail-ops">
        <button onClick={() => setViewRoute(null)} className="mb-3 text-sm text-slate-500">← Back to planning</button>
        <h2 className="text-2xl font-bold text-[#0E2A47]">{r.route_number} {r.route_name && <span className="text-slate-400">· {r.route_name}</span>}</h2>
        <p className="text-sm text-slate-500">{r.date} · {r.technician_name}{r.technician_phone ? ` · ${r.technician_phone}` : ''} · <span className="font-semibold capitalize">{r.status.replace('_', ' ')}</span> · {r.progress.done}/{r.progress.total} done</p>
        <div className="mt-4 space-y-2">
          {r.stops.map(s => (
            <div key={s.stop_id} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`ops-stop-${s.stop_id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-[#0E2A47]">{s.order}. {s.location_name} <span className="font-mono text-xs text-slate-400">#{s.account_number || '—'}</span></div>
                <span className="rounded-full px-2 py-0.5 text-xs font-semibold text-white capitalize" style={{ backgroundColor: STOP_COLORS[s.status] }}>{s.status.replace('_', ' ')}</span>
              </div>
              <div className="text-xs text-slate-500">{s.address}</div>
              {s.location_notes && <div className="mt-1 text-sm text-slate-700" data-testid={`ops-stop-notes-${s.stop_id}`}>Notes: {s.location_notes}</div>}
              {s.instructions && <div className="mt-1 text-sm text-slate-700">{s.instructions}</div>}
              {s.tasks?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`ops-stop-tasks-${s.stop_id}`}>
                  {s.tasks.map(t => (
                    <span key={t.id} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                      {t.done && <Check size={11} className="text-[#5FA718]" />}{t.label}
                      <span className="rounded bg-[#1C4FC9]/10 px-1 text-[10px] font-semibold text-[#1C4FC9]">{ACTION_LABELS[t.action] || t.action}</span>
                    </span>
                  ))}
                </div>
              )}
              {s.checklist?.length > 0 && <div className="mt-1 text-xs text-slate-600">{s.checklist.map(c => `${c.done ? '☑' : '☐'} ${c.text}`).join('  ·  ')}</div>}
              {s.note && <div className="mt-1 text-xs italic text-slate-500">Note: {s.note}</div>}
              {s.unable_reason && <div className="mt-1 text-xs text-[#C8641E]">Unable: {s.unable_reason}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="route-planning">
      <div className="mb-4">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Dispatch</div>
        <h1 className="text-3xl font-extrabold text-[#0E2A47]">Route Planning &amp; Assignments</h1>
        <p className="text-sm text-slate-500">Group nearby service locations into a dated route and assign it to a field technician. Map is OpenStreetMap; stop order is as you arrange it (not auto-optimized).</p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Left: list + selection */}
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[160px]"><Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter service locations…" data-testid="rp-search" className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-sm" /></div>
            <button onClick={resetDraft} data-testid="rp-reset" className="inline-flex items-center gap-1 rounded-lg border border-[#C8641E] px-3 py-2 text-sm font-semibold text-[#C8641E] hover:bg-orange-50"><RotateCcw size={14} /> Reset</button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="rp-geo-filters">
            <select value={fCounty} onChange={e => setFCounty(e.target.value)} data-testid="rp-filter-county" className="min-h-9 rounded-lg border border-slate-200 px-2 text-sm"><option value="">All counties</option>{counties.map(c => <option key={c} value={c}>{c}</option>)}</select>
            <select value={fCity} onChange={e => setFCity(e.target.value)} data-testid="rp-filter-city" className="min-h-9 rounded-lg border border-slate-200 px-2 text-sm"><option value="">All cities</option>{cities.map(c => <option key={c} value={c}>{c}</option>)}</select>
            <input value={fZip} onChange={e => setFZip(e.target.value)} placeholder="ZIP" data-testid="rp-filter-zip" className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-sm" />
          </div>
          <div className="h-[460px] overflow-auto rounded-lg border border-slate-200 lg:h-[600px]" data-testid="rp-location-list">
            {filtered.map(l => (
              <button key={l.id} onClick={() => toggle(l)} data-testid={`rp-loc-${l.id}`} className={`flex w-full items-center gap-2 border-b border-slate-100 p-2.5 text-left hover:bg-slate-50 ${isSel(l.id) ? 'bg-[#8FC93A]/10' : ''}`}>
                <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${isSel(l.id) ? 'border-[#5FA718] bg-[#5FA718] text-white' : 'border-slate-300'}`}>{isSel(l.id) && <Check size={13} />}</span>
                <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-[#0E2A47]">{l.location_name} <span className="font-mono text-xs text-slate-400">#{l.account_number || '—'}</span></span>
                  <span className="block truncate text-xs text-slate-500">{l.address || <span className="text-[#C8641E]">No address on file</span>}</span></span>
                {l.geo_state === 'incomplete' ? <AlertTriangle size={14} className="text-[#C8641E]" title="Incomplete address" />
                  : l.geo_state === 'mapped' ? <MapPin size={13} className="text-[#5FA718]" title="Mapped" />
                  : l.geo_state === 'rate_limited' ? <Clock3 size={13} className="text-sky-500" title="Map service busy — select to retry shortly" />
                  : l.geo_state === 'failed' ? <AlertTriangle size={14} className="text-amber-500" title="Geocoding failed — select to retry" />
                  : <Clock3 size={13} className="text-amber-500" title="Needs geocoding — select to plot" />}
              </button>
            ))}
            {filtered.length === 0 && <div className="p-4 text-sm text-slate-400">No locations match.</div>}
          </div>
          {incomplete.length > 0 && <p className="text-xs text-[#C8641E]" data-testid="rp-incomplete">{incomplete.length} location(s) have incomplete addresses and are not placed on the map.</p>}
        </div>

        {/* Right: map */}
        <div className="min-w-0 space-y-1">
          <div className="relative">
            <RouteMap points={mapPoints} onPick={onMapPick} focusId={focusId} fitSignature={fitSignature} heightClass="h-[460px] lg:h-[600px]" />
            <button onClick={() => setExpanded(true)} data-testid="rp-expand-map" className="absolute right-2 top-2 z-[1000] inline-flex items-center gap-1 rounded-lg bg-white/95 px-2.5 py-1.5 text-xs font-semibold text-[#0E2A47] shadow hover:bg-white"><Maximize2 size={13} /> Expand</button>
          </div>
          <p className="text-right text-[10px] text-slate-400" data-testid="rp-geo-attribution">
            Geocoding: OpenStreetMap Nominatim · <a href="https://www.geoapify.com/" target="_blank" rel="noreferrer" className="underline hover:text-slate-600">Powered by Geoapify</a>
          </p>
        </div>
      </div>

      {expanded && (
        <div className="fixed inset-0 z-[2000] flex flex-col bg-white p-3" data-testid="rp-map-expanded">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold text-[#0E2A47]">Route map · {selected.length} selected stop{selected.length === 1 ? '' : 's'}</div>
            <button onClick={() => setExpanded(false)} data-testid="rp-collapse-map" className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50"><Minimize2 size={14} /> Close</button>
          </div>
          <div className="min-h-0 flex-1">
            <RouteMap points={mapPoints} onPick={onMapPick} focusId={focusId} fitSignature={`exp-${fitSignature}`} heightClass="h-full" />
          </div>
          <p className="mt-1 text-right text-[10px] text-slate-400">Geocoding: OpenStreetMap Nominatim · Powered by Geoapify</p>
        </div>
      )}

      {/* Build route */}
      {selected.length > 0 && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="rp-builder">
          <h3 className="mb-1 flex items-center gap-2 font-bold text-[#0E2A47]"><RouteIcon size={18} /> Route Draft ({selected.length} stops)</h3>
          {editingTemplateId
            ? <p className="mb-3 inline-flex items-center gap-1 rounded-full bg-[#1C4FC9]/10 px-2.5 py-0.5 text-xs font-semibold text-[#1C4FC9]" data-testid="rp-editing-template"><Pencil size={12} /> Editing template · changes won't affect already-assigned routes</p>
            : <p className="mb-3 text-xs text-slate-500">Stop order below = the order you clicked. Save as a reusable template and/or assign it as a dated route.</p>}
          <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-400">Assign as a dated route</div>
          <div className="grid gap-3 sm:grid-cols-4">
            <input value={routeNo} onChange={e => setRouteNo(e.target.value)} placeholder="Route name *" data-testid="rp-route-number" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input value={routeName} onChange={e => setRouteName(e.target.value)} placeholder="Description (optional)" data-testid="rp-route-name" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="rp-date" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
            <select value={techId} onChange={e => setTechId(e.target.value)} data-testid="rp-tech" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <option value="">Assign technician *</option>
              {techs.map(t => <option key={t.id} value={t.id}>{t.name}{t.phone ? ` · ${t.phone}` : ''}</option>)}
            </select>
          </div>
          <div className="mt-3 space-y-2">
            {selected.map((s, i) => (
              <div key={s.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`rp-stop-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-[#0E2A47]">{i + 1}. {s.location_name}</div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(i, -1)} className="rounded border border-slate-200 p-1 text-slate-500 hover:bg-white"><ChevronUp size={14} /></button>
                    <button onClick={() => move(i, 1)} className="rounded border border-slate-200 p-1 text-slate-500 hover:bg-white"><ChevronDown size={14} /></button>
                    <button onClick={() => toggle(s)} className="rounded border border-slate-200 p-1 text-red-500 hover:bg-white"><X size={14} /></button>
                  </div>
                </div>
                <div className="truncate text-xs text-slate-500">{s.address}</div>
                {s.tasks?.length > 0 && <div className="mt-1 text-[11px] font-semibold text-[#5FA718]">{s.tasks.length} task{s.tasks.length === 1 ? '' : 's'} selected</div>}
                <RouteStopTasks
                  stopId={s.id}
                  tasks={s.tasks || []}
                  notes={s.notes || ''}
                  onTasksChange={(tasks) => setStopField(s.id, 'tasks', tasks)}
                  onNotesChange={(notes) => setStopField(s.id, 'notes', notes)}
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex flex-1 flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[180px]">
                <label className="mb-1 block text-xs font-semibold text-slate-500">Route Template</label>
                <input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name (e.g. Naples Banks – Weekly)" data-testid="rp-template-name" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
              <button onClick={saveTemplate} disabled={savingTpl} data-testid="rp-save-template" className="inline-flex items-center gap-2 rounded-lg border border-[#1C4FC9] px-4 py-2.5 text-sm font-semibold text-[#1C4FC9] hover:bg-[#1C4FC9]/5 disabled:opacity-60">{savingTpl ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {editingTemplateId ? 'Update Template' : 'Save Template'}</button>
            </div>
            <button onClick={save} disabled={saving} data-testid="rp-save" className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#5FA718] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#4f8d14] disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Save &amp; Assign Route</button>
          </div>
        </div>
      )}

      {/* Saved templates */}
      <div className="mt-6">
        <h3 className="mb-2 flex items-center gap-2 font-bold text-[#0E2A47]"><FolderOpen size={18} /> Route Templates</h3>
        {templates.length === 0 ? <p className="text-sm text-slate-400">No templates yet. Select stops, give it a name, and hit Save Template.</p> : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="rp-templates-list">
            {templates.map(t => (
              <div key={t.id} data-testid={`rp-template-${t.id}`} className={`flex items-center justify-between rounded-lg border bg-white p-3 ${editingTemplateId === t.id ? 'border-[#1C4FC9]' : 'border-slate-200'}`}>
                <button onClick={() => loadTemplate(t.id)} data-testid={`rp-template-open-${t.id}`} className="min-w-0 flex-1 text-left">
                  <div className="truncate font-semibold text-[#0E2A47]">{t.name}</div>
                  <div className="text-xs text-slate-500">{t.stop_count} stop{t.stop_count === 1 ? '' : 's'}{t.description ? ` · ${t.description}` : ''}</div>
                </button>
                <div className="ml-2 flex flex-shrink-0 items-center gap-1">
                  <button onClick={() => loadTemplate(t.id, true)} data-testid={`rp-template-assign-${t.id}`} title="Assign this template as a dated route" className="inline-flex items-center gap-1 rounded border border-[#5FA718] px-2 py-1 text-xs font-semibold text-[#5FA718] hover:bg-[#5FA718]/10"><CalendarPlus size={13} /> Assign</button>
                  <button onClick={() => loadTemplate(t.id)} data-testid={`rp-template-edit-${t.id}`} title="Open to edit" className="rounded border border-slate-200 p-1.5 text-[#1C4FC9] hover:bg-slate-50"><Pencil size={13} /></button>
                  <button onClick={() => deleteTemplate(t.id, t.name)} data-testid={`rp-template-delete-${t.id}`} title="Delete template" className="rounded border border-slate-200 p-1.5 text-red-500 hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Existing routes */}
      <div className="mt-6">
        <h3 className="mb-2 font-bold text-[#0E2A47]">Assigned Routes</h3>
        {routes.length === 0 ? <p className="text-sm text-slate-400">No routes yet.</p> : (
          <div className="grid gap-2" data-testid="rp-routes-list">
            {routes.map(r => (
              <button key={r.id} onClick={() => setViewRoute(r)} data-testid={`rp-route-${r.id}`} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-[#8FC93A]">
                <div><div className="font-semibold text-[#0E2A47]">{r.route_number} {r.route_name && <span className="text-slate-400">· {r.route_name}</span>}</div>
                  <div className="text-xs text-slate-500">{r.date} · {r.technician_name} · {r.progress.done}/{r.progress.total} stops done</div></div>
                <span className="rounded-full px-2.5 py-1 text-xs font-semibold text-white capitalize" style={{ backgroundColor: STOP_COLORS[r.status] }}>{r.status.replace('_', ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoutePlanning;
